import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { saveAs } from "file-saver";
import { useMemo, useState } from "react";

import {
  FullScreenModal,
  FullScreenModalBody,
  FullScreenModalHeader,
  Loader,
  Notification,
  NotificationType,
  TextTooltip,
} from "@transfr-inc/dashboard-components";
import { Product } from "@transfr-inc/dashboard-components/constants";
import {
  FormTable,
  SortDirection,
} from "@transfr-inc/dashboard-components/data-table";
import { Button, FileInput } from "@transfr-inc/dashboard-components/forms";
import { Color, getProductName } from "@transfr-inc/dashboard-components/utils";

import { CancelModal } from "@components/modals";
import { useContainer } from "@src/contexts/container-context";
import { useCurrentUser, useRole } from "@queries";
import { useClassrooms } from "@queries/organization";
import { getErrorMessage } from "@lib/browser-utils";
import { getLearnerTerm } from "@transfr-inc/dashboard-components/constants";
import {
  cancelBodyText,
  cancelBodyTitle,
  convertToCSV,
  DataTableColumnDefinition,
  TEMPLATE_DOWNLOAD_INFO_TEXT,
} from "./utils";
import { ZeroStateMessage } from "./zero-state-message";

import "./roster-modal.scss";

const downloadBulkUploadTemplate = (classroomId, product) => {
  if (classroomId) {
    // Point programs classrooms to the train template
    if (product == Product.PROG) {
      product = Product.TS;
    }
    const productSuffix = getProductName(product)?.toLowerCase();
    saveAs(
      `/templates/bulk-user-upload-template.${productSuffix}.csv`,
      "roster-user-upload-template.csv"
    );
  } else {
    saveAs(
      `/templates/roster-user-upload-template.csv`,
      "roster-user-upload-template.csv"
    );
  }
};

export const RosterModal = ({
  onClose,
  show,
  responsiveStyles,
  classroomId,
  product = Product.TS,
}) => {
  const currentUser = useCurrentUser();
  const role = useRole();
  const { data: classrooms = [] } = useClassrooms(
    {
      userId: currentUser?.userId,
      role,
      orgCode: currentUser?.organizationCode,
    },
    {
      enabled: !!(currentUser?.userId && role && currentUser?.organizationCode),
    }
  );

  const [loading, setLoading] = useState();
  const [template, setTemplate] = useState();
  const [showCancelModal, setShowCancelModal] = useState();
  const [uploadNotification, setUploadNotification] = useState(false);
  const [rowToAdd, setRowToAdd] = useState();
  const [takenUsernames, setTakenUsernames] = useState(new Set());
  const [takenEmails, setTakenEmails] = useState(new Set());
  const [conflictVersion, setConflictVersion] = useState(0);

  const { userService } = useContainer();

  const classroomCodes = useMemo(() => {
    let classroomMap = [];
    if (classrooms?.length) {
      classroomMap = classrooms
        .filter((classroom) => !classroom.isProfessionalLearning)
        .map((classroom) => ({
          id: classroom.code,
          name: classroom.code,
          description: classroom.name,
        }));
    }
    return classroomMap;
  }, [classrooms]);

  const columns = useMemo(
    () => [
      DataTableColumnDefinition.FIRST_NAME,
      DataTableColumnDefinition.LAST_NAME,
      ...(!classroomId
        ? [DataTableColumnDefinition.CLASSROOM_CODE(classroomCodes)]
        : []),
      DataTableColumnDefinition.USERNAME(takenUsernames),
      DataTableColumnDefinition.EMAIL(takenEmails),
      DataTableColumnDefinition.PASSWORD,
    ],
    [classroomCodes, classroomId, takenUsernames, takenEmails]
  );

  const onConfirmPublishClick = async ({ formData }) => {
    setLoading(true);

    try {
      const { usernames, emails } = formData.reduce(
        (acc, row) => {
          if (row.username) acc.usernames.push(row.username);
          if (row.email) acc.emails.push(row.email);
          return acc;
        },
        { usernames: [], emails: [] }
      );
      const { taken_usernames: takenUsernamesResult, taken_emails: takenEmailsResult } =
        await userService.checkAvailability({ usernames, emails });

      if (takenUsernamesResult.length || takenEmailsResult.length) {
        // Preserve user edits by writing current form state back to template,
        // then remount FormTable (via conflictVersion key) so react-hook-form
        // re-validates all fields against the updated validate.taken rules.
        setTemplate(formData);
        setTakenUsernames(
          new Set(takenUsernamesResult.map((u) => u.trim().toLowerCase()))
        );
        setTakenEmails(
          new Set(takenEmailsResult.map((e) => e.trim().toLowerCase()))
        );
        setConflictVersion((v) => v + 1);
        setUploadNotification({
          message:
            `${
              takenUsernamesResult.length + takenEmailsResult.length
            } conflict(s) found. ` +
            `Update the highlighted rows and publish again.`,
          type: NotificationType.error,
        });
        setLoading(false);
        return;
      }
    } catch (error) {
      setUploadNotification({
        message: getErrorMessage(error.data),
        type: NotificationType.error,
      });
      setLoading(false);
      return;
    }

    const mappedData = formData.map((row) => ({
      "First Name": row.firstName,
      "Last Name": row.lastName,
      ...(!classroomId ? { "Classroom Code": row.classroomCode?.id } : {}),
      Username: row.username,
      Email: row.email,
      Password: row.password,
    }));

    const file = convertToCSV(mappedData);

    // eslint-disable-next-line no-undef
    const formDataToUpload = new FormData();
    formDataToUpload.append("file", file, "roster.csv");
    formDataToUpload.append("organization_code", currentUser?.organizationCode);
    classroomId && formDataToUpload.append("classroom_id", classroomId);

    try {
      const isValidCsv = await userService.uploadUsersAsync(
        formDataToUpload,
        currentUser?.organizationCode,
        undefined,
        true
      );
      if (isValidCsv == true) {
        setUploadNotification({
          message:
            `Your file has been successfully uploaded. Due to its size this file will ` +
            `continue to process in the background. Feel free to continue and we will ` +
            `send you an email when it's finished. `,
          type: NotificationType.success,
        });
        onCancel();
      }
    } catch (error) {
      setUploadNotification({
        message: getErrorMessage(error.data),
        type: NotificationType.error,
      });
    }
    setLoading(false);
  };

  const onHandleFile = async (file) => {
    setTakenUsernames(new Set());
    setTakenEmails(new Set());
    setLoading(true);
    try {
      const parsedCsv = await userService.validateRosterCsv(
        file,
        currentUser?.organizationCode
      );
      if (parsedCsv) {
        const mappedCsv = parsedCsv.map((csv) => {
          if (csv?.classroomCode) {
            csv.classroomCode = {
              id: csv?.classroomCode,
              name: csv?.classroomCode,
              description: csv?.classroomCode,
            };
          }
          return csv;
        });
        setTemplate(mappedCsv);
        setUploadNotification({
          message: "File imported successfully.",
          type: NotificationType.success,
        });
      }
    } catch (error) {
      setUploadNotification({
        message: getErrorMessage(error.data),
        type: NotificationType.error,
      });
    }
    setLoading(false);
  };

  const onCancel = () => {
    setTemplate();
    setShowCancelModal();
    onClose();
  };

  const onAddRow = () => {
    setRowToAdd({
      firstName: "",
      lastName: "",
      classroomCode: "",
      username: "",
      email: "",
      password: "",
    });
  };

  const handleCloseModal = () => {
    // FullScreenModal calls its onClose handler on first render, so we'll only
    // show the cancel modal if the roster modal is actually open.
    if (show) {
      setShowCancelModal(true);
    }
  };

  return (
    <>
      <FullScreenModal
        className="roster-modal"
        onClose={handleCloseModal}
        open={show}
        drawerBgColor={Color.white}
      >
        <FullScreenModalHeader
          title="Roster"
          titleIcon="fa-regular fa-clipboard-list-check"
        />
        <FullScreenModalBody>
          <div className="roster-modal-body">
            {loading && <Loader overlay></Loader>}
            <div>
              <span className="roster-modal-description">
                {`Quickly create ${getLearnerTerm({
                  lowercase: true,
                  plural: true,
                })} ${
                  classroomId
                    ? "for this classroom"
                    : "across your organization"
                }. If a ${getLearnerTerm({
                  lowercase: true,
                })} already exists their information will be updated.`}
              </span>
            </div>
            <div className="roster-action-button-row">
              <div className="left-group">
                <Button
                  icon={["fa-light", "download"]}
                  onClick={() =>
                    downloadBulkUploadTemplate(classroomId, product)
                  }
                >
                  Template (.csv)
                </Button>
                <FileInput
                  primary={false}
                  size="small"
                  icon={["fa-regular", "file-import"]}
                  onHandleFile={onHandleFile}
                  className={"main-dialog"}
                  disabled={loading}
                  loader={loading}
                  label={"Import (.csv)"}
                ></FileInput>
                <TextTooltip text={TEMPLATE_DOWNLOAD_INFO_TEXT}>
                  <FontAwesomeIcon icon="fa-regular fa-circle-info" />
                </TextTooltip>
              </div>
              <div className="right-group">
                <Button icon={["fa-regular", "plus-circle"]} onClick={onAddRow}>
                  Add Row
                </Button>
              </div>
            </div>
            <div
              className={clsx(
                "roster-list-container",
                classroomId && "classroom-upload"
              )}
            >
              <FormTable
                key={conflictVersion}
                data={template}
                columns={columns}
                showValidationStatus
                rowHeightConfig={{ large: 80, medium: 160, small: 280 }}
                virtualRowEnabled={false}
                hasRemoveColumn
                defaultSortPropName={"firstName"}
                defaultSortDirection={SortDirection.DESCENDING}
                onCancel={handleCloseModal}
                handleSubmitForm={onConfirmPublishClick}
                responsiveStyles={responsiveStyles}
                showBottomBarOptions
                zeroStateMessage={<ZeroStateMessage loading={loading} />}
                rowToAdd={rowToAdd}
                setRowToAdd={setRowToAdd}
              />
            </div>
          </div>
        </FullScreenModalBody>
        {uploadNotification && (
          <div className="notifications-area">
            <div className="notification-container">
              <Notification
                type={uploadNotification.type}
                closable
                onClose={() => {
                  setUploadNotification();
                }}
              >
                {uploadNotification.message}
              </Notification>
            </div>
          </div>
        )}
        <CancelModal
          open={showCancelModal}
          onCancel={() => setShowCancelModal()}
          title={cancelBodyTitle}
          body={cancelBodyText}
          cancelText="No"
          acceptText="Yes, Cancel"
          onAccept={onCancel}
        />
      </FullScreenModal>
    </>
  );
};
